import { useState } from "react";
import { t } from "@/i18n-js/instance";
import { SettingsHeader } from "@/react/components/SettingsApp/SettingsHeader";
import { usePunditUserContext } from "@/react/contexts";
import { useSmOrMdOrLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { toLocaleString } from "@circle-react/helpers/number";
import { botBuilderNavPaths } from "@circle-react/helpers/urlHelpers/botBuilderNavPaths";
import {
  USAGE_SECTIONS,
  UsageContextProvider,
} from "@circle-react/providers/UsageContext";
import type {
  BaseFilter,
  ProfileFieldBaseFilter,
} from "@circle-react-shared/MemberFilters/types";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { DataTable } from "@circle-react-shared/uikit/DataTableV2";
import { Loader } from "@circle-react-shared/uikit/Loader";
import { Button } from "../../shared/uikit/Button";
import { useToast } from "../../shared/uikit/ToastV2";
import { AgentAvatar } from "./AgentAvatar";
import { AgentEnableSwitch } from "./AgentEnableSwitch";
import { AgentsBanner } from "./AgentsBanner";
import { AgentsDropdownActions } from "./AgentsDropdownActions";
import { AnswerTypeIcon } from "./AnswerTypeIcon";
import { AudienceTypeIcon } from "./AudienceIcon";
import { BehaviorTypeIcon } from "./BehaviorTypeIcon";
import { LandingPage } from "./LandingPage";
import { MobileDashboard } from "./MobileDashboard";
import { NoAgents } from "./NoAgents";
import { useCreateAgent, useIndexAgents } from "./agentsQueries";
import { useShowSetting } from "./queries";

export interface AgentProp {
  allow_direct_messages: boolean;
  allow_search_conversations: boolean;
  answer_type: "ai-generated" | "custom";
  avatar_url: string;
  created_at: string;
  enabled: boolean;
  id: number;
  members_filters?: Array<BaseFilter | ProfileFieldBaseFilter>;
  name: string;
  posts_count: number;
  purpose: string;
  send_welcome_message: boolean;
  snippets_count: number;
  tone: "normal" | "friendly" | "short-and-sweet" | "descriptive";
  updated_at: string;
  use_all_kb_posts: boolean;
  use_community_knowledge: boolean;
  use_custom_snippets: boolean;
  use_platform_support: boolean;
}

const columns = [
  {
    header: t("settings.community_bot.agents.columns.name"),
    accessorKey: "name",
    cell: ({ row }: any) => (
      <div className="ml-2 flex items-center gap-4">
        {row.original?.avatar_url ? (
          <img
            src={row.original.avatar_url}
            alt={t("community_bot.bot_builder.customize_page.avatar")}
            loading="eager"
            className="size-8 rounded-full"
            width={32}
            height={32}
          />
        ) : (
          <AgentAvatar />
        )}
        <span className="font-[InterVariable,sans-serif] font-semibold">
          {row.original?.name}
        </span>
        <div className="hidden items-center gap-1 md:flex">
          <TippyV2
            content={t(
              "settings.community_bot.agents.ai_generated_answers_button.title",
            )}
          >
            <AnswerTypeIcon answerType={row.original?.answer_type} />
          </TippyV2>
          <TippyV2 content={row.original?.members_filters_tooltip}>
            <AudienceTypeIcon />
          </TippyV2>
          <TippyV2
            content={t(
              `settings.community_bot.agents.behavior_type_button.options.${row.original?.tone.replaceAll("-", "_")}`,
            )}
          >
            <BehaviorTypeIcon tone={row.original?.tone} />
          </TippyV2>
        </div>
      </div>
    ),
    canSort: false,
  },
  {
    header: t("settings.community_bot.agents.columns.conversations"),
    accessorKey: "conversations",
    cell: ({ row }: any) => toLocaleString(row.original?.conversations || 0),
    canSort: false,
  },
  {
    header: t("settings.community_bot.agents.columns.messages"),
    accessorKey: "messages",
    cell: ({ row }: any) => toLocaleString(row.original?.messages || 0),
    canSort: false,
  },
  {
    header: t("settings.community_bot.agents.columns.modified_on"),
    accessorKey: "modified_on",
    cell: ({ row }: any) => row.original?.modified_on,
    canSort: false,
  },
  {
    header: t("settings.community_bot.agents.columns.active"),
    accessorKey: "active",
    cell: ({ row }: any) => (
      <div className="flex w-16 justify-start">
        <AgentEnableSwitch row={row} />
      </div>
    ),
    canSort: false,
  },
  {
    accessorKey: "actions",
    header: "",
    cell: ({ row }: any) => <AgentsDropdownActions agent={row.original} />,
  },
];

export const CommunityBotAgents = () => {
  const [currentPage, setPage] = useState(1);
  const settingQuery = useShowSetting();
  const agentsLimit = settingQuery.data?.agents_limit;

  const agentsQuery = useIndexAgents({
    currentPage,
    perPage: agentsLimit ?? undefined,
    enabled: agentsLimit !== undefined,
  });
  const isMobileView = useSmOrMdOrLgScreenMediaQuery();
  const { currentCommunity } = usePunditUserContext();

  const onChangePage = (page: number) => {
    // Forcing useQuery to only cache one page
    setPage(page);
    agentsQuery.remove();
  };

  const createAgentMutation = useCreateAgent();

  const agents = agentsQuery?.data?.records ?? [];
  const hasNoAgents = agents.length === 0;
  const isAgentCreationDisabled =
    agents.length >= (settingQuery?.data?.agents_limit ?? 10);

  const isFeatureDisabled = !currentCommunity?.ai_agents_feature_flag_enabled;
  const shouldShowLandingPage = isFeatureDisabled && hasNoAgents;
  const shouldShowEmptyState = !isFeatureDisabled && hasNoAgents;

  const toast = useToast();

  if (isMobileView) {
    return <MobileDashboard />;
  }

  if (agentsQuery.isLoading || settingQuery.isLoading) {
    return <Loader />;
  }

  if (shouldShowLandingPage) {
    return (
      <UsageContextProvider section={USAGE_SECTIONS.ADMIN}>
        <LandingPage />
      </UsageContextProvider>
    );
  }

  if (shouldShowEmptyState) {
    return (
      <UsageContextProvider section={USAGE_SECTIONS.ADMIN}>
        <NoAgents />
      </UsageContextProvider>
    );
  }

  const handleEditAgent = (agent: AgentProp) => {
    window.location.href = botBuilderNavPaths.customize({
      botPublicId: agent.id.toString(),
    });
  };

  const handleCreateAgent = () => {
    createAgentMutation.mutate(undefined, {
      onSuccess: data => {
        window.location.href = botBuilderNavPaths.customize({
          botPublicId: data.id.toString(),
        });
      },
      onError: (data: Error) => {
        toast.error(data.message);
      },
    });
  };

  return (
    <UsageContextProvider section={USAGE_SECTIONS.ADMIN}>
      <div className="bg-primary flex h-full flex-col">
        <SettingsHeader
          pageTitle={t("settings.community_bot.agents.title")}
          rightContent={
            <TippyV2
              disabled={!isAgentCreationDisabled}
              content={t(
                "settings.community_bot.agents.validation.max_profiles_reached",
                {
                  limit: settingQuery?.data?.agents_limit ?? 10,
                },
              )}
            >
              <Button
                variant="circle"
                onClick={handleCreateAgent}
                disabled={isAgentCreationDisabled}
              >
                {t("settings.community_bot.agents.new_agent")}
              </Button>
            </TippyV2>
          }
        />
        {!settingQuery?.data?.hide_agents_banner && (
          <AgentsBanner
            agents={agentsQuery?.data?.records ?? []}
            handleEditAgent={handleEditAgent}
          />
        )}
        <DataTable.TableWrapper>
          <DataTable
            columns={columns}
            onClickRow={({ original, clickEvent }) => {
              if (clickEvent && clickEvent.target) {
                const wrapper = document.getElementById(
                  `toggle-wrapper-agent-${original.id}`,
                );

                if (!wrapper?.contains(clickEvent.target)) {
                  handleEditAgent(original);
                }
              }
            }}
            data={agentsQuery?.data?.records ?? []}
            isLoading={agentsQuery?.isLoading}
            initialPage={agentsQuery?.data?.page}
            pageSize={agentsQuery?.data?.per_page}
            totalRecordsCount={agentsQuery?.data?.count}
            onChangePage={onChangePage}
          >
            <DataTable.Table>
              <DataTable.Header />
              <DataTable.Loader />
              <DataTable.Body />
            </DataTable.Table>
          </DataTable>
        </DataTable.TableWrapper>
      </div>
    </UsageContextProvider>
  );
};
